/**
 * FeaturesSection Component
 * Key features highlights
 */

import { Activity, Search, Sparkles, TrendingUp } from 'lucide-react';
import type { ReactElement } from 'react';

interface Feature {
  icon: ReactElement;
  title: string;
  description: string;
}

const FEATURES: Feature[] = [
  {
    icon: <Sparkles className="w-8 h-8" />,
    title: 'AI Pick Generation',
    description:
      'Generate data-driven basketball parlays across multiple risk tiers. Configure analysis factors (trends, matchups, pace, defense) and let AI find the edges.',
  },
  {
    icon: <Activity className="w-8 h-8" />,
    title: 'Real-Time Tracking',
    description:
      'Track your parlays with automatic settlement. See live stats, get instant notifications when games complete, and watch your portfolio in real-time.',
  },
  {
    icon: <TrendingUp className="w-8 h-8" />,
    title: 'Performance Analytics',
    description:
      'Detailed insights on your betting performance. Track hit rates by prop type, identify your strengths, and see which analysis factors drive your best picks.',
  },
  {
    icon: <Search className="w-8 h-8" />,
    title: 'Best Line Finder',
    description:
      'Compare odds across multiple sportsbooks instantly. Find the best available lines for every prop to maximize your edge and expected value.',
  },
];

export function FeaturesSection(): ReactElement {
  return (
    <section id="features" className="py-20 bg-theme-surface">
      <div className="max-w-6xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-theme-text mb-4">
            Everything You Need to Win
          </h2>
          <p className="text-lg text-theme-text-secondary max-w-2xl mx-auto">
            Built for serious basketball bettors who want data-driven picks, not hunches. From
            generation to settlement, we&apos;ve got you covered.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {FEATURES.map((feature, index) => (
            <div
              key={index}
              className="p-6 rounded-xl bg-theme-bg border border-theme-border hover:border-theme-primary transition group"
            >
              <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-theme-primary to-theme-primary-hover flex items-center justify-center text-white mb-4 group-hover:scale-110 transition">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-theme-text mb-2">{feature.title}</h3>
              <p className="text-theme-text-secondary leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
